import { put, select, call } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";

import {
  commonSlice,
  selectActiveTab,
  selectNavigationTabs,
} from "@/entities/common";

import { StorageKeys } from "@/shared/types/enums/StorageKeys.enum";
import {
  AlertStatusMessage, DialogMode, IApiError,
  ICart,
  ITabItem,
  ToggleActionType,
  UserRole,
} from '@/shared/types';
import {
  setItemToLS,
  removeItemFromLS,
} from "@/shared/utils/localStorageHelpers";

import { getCurrentUser, getNavigationTabsByUserRole } from "./helpers";
import { UserPayload, ActiveTabValuePayload, CardPayload } from "./types/types";
import { selectUserCart } from '@/entities/common';
import {
  sendCreateCartRequest,
  sendAddItemToCartRequest,
  sendAddInfectiousItemToCartRequest,
  sendGetInfectiousOrderItemsRequest,
  sendRemoveInfectiousItemFromCartRequest,
  sendRemoveItemFromCartRequest, sendPlaceOrderRequest, sendPresignedImageUrlRequest, sendRemoveAllItemFromCartRequest,
} from './senders';
import { dialogSagaActions } from "@/widgets/dialog";
import { alertsSagaActions } from '@/entities/alerts';
import { UpdateUserAvatarPayload } from '@/entities/users/api/types/types';
import { t } from 'i18next';
import { checkUserByToken } from '@/entities/auth';
import {
  getCartInfectiousOrderItems,
  getCartItemsCount,
  mergeInfectiousOrderItemsIntoCart,
} from '@/shared/utils/cartHelpers';

export function* handleUserLoggedIn(action: PayloadAction<UserPayload>) {
  const { setCurrentUser, setNavigationTabs, setActiveTab } = commonSlice;
  const currentUser = getCurrentUser(action.payload.user);
  const navigationTabs: Array<ITabItem> = yield call(
    getNavigationTabsByUserRole,
    currentUser.role
  );
  const activeTab: ITabItem = yield select(selectActiveTab);
  yield put(setCurrentUser({ currentUser }));
  yield put(setNavigationTabs({ navigationTabs }));
  if (!activeTab) {
    yield call(
      setItemToLS,
      StorageKeys.ACTIVE_TAB,
      JSON.stringify(navigationTabs[0])
    );
    yield put(setActiveTab({ activeTab: navigationTabs[0] }));
  }
}

export function* handleUserLoggedOut() {
  const { setActiveTab, setNavigationTabs, setCurrentUser, setLoading } = commonSlice;
  yield put(setActiveTab({ activeTab: null }));
  yield put(setCurrentUser({ currentUser: { role: UserRole.UNAUTHORIZED } }));
  yield put(setNavigationTabs({ navigationTabs: [] }));
  yield put(setLoading({ isLoading: false }));
  yield call(removeItemFromLS, StorageKeys.ACTIVE_TAB);
}

export function* handleUpdateActiveTab(
  action: PayloadAction<ActiveTabValuePayload>
) {
  const { setActiveTab } = commonSlice;
  const { value } = action.payload;
  const navigationTabs: Array<ITabItem> = yield select(selectNavigationTabs);
  const activeTab = navigationTabs[value];
  const cart: ICart | null = yield select(selectUserCart);

  // Prevent navigation to Cart if it's empty
  if (activeTab.name === "Cart" && getCartItemsCount(cart) === 0) {
    return;
  }

  yield put(setActiveTab({ activeTab }));
  yield call(setItemToLS, StorageKeys.ACTIVE_TAB, JSON.stringify(activeTab));
}

export type CreateCartResponse = {
  data: {
    data: ICart;
  };
};

export type AddItemToCartResponse = {
  data: {
    data: ICart;
  };
};

export type RemoveItemFromCartResponse = {
  data: {
    data: ICart;
  };
};

export type placeOrderResponse = {
  data: {
    data: ICart;
  };
};

// export const modifyCartData = (cart: ICart) => {
//   const samples = cart.samples;

//   const products = samples.map((sample: ISample) => {
//     return { ...sample, hasWatched: `${item.firstName} ${item.lastName}` };
//   });
// };

export function* handleAddSampleToCart(action: PayloadAction<CardPayload>) {
  const { setUserCart } = commonSlice;
  let cart: ICart | null = yield select(selectUserCart);

  if (!cart) {
    const response: CreateCartResponse = yield call(sendCreateCartRequest);
    cart = response.data.data;
  }

  if (getCartInfectiousOrderItems(cart).length > 0) {
    return;
  }

  const response2: AddItemToCartResponse = yield call(
    sendAddItemToCartRequest,
    cart.id as string,
    action.payload
  );
  cart = response2.data.data;

  yield put(setUserCart({ cart }));
}

export function* handleAddInfectiousAliquotToCart(
  action: PayloadAction<CardPayload>
): Generator<any, void, any> {
  const { setUserCart } = commonSlice;
  let cart: ICart | null = yield select(selectUserCart);

  if (!cart) {
    const response: CreateCartResponse = yield call(sendCreateCartRequest);
    cart = response.data.data;
  }

  if (cart.samples.length > 0) {
    return;
  }

  const aliquotIds = action.payload.aliquotIds?.length
    ? action.payload.aliquotIds
    : [action.payload.aliquotId ?? action.payload.sampleId];

  for (const aliquotId of aliquotIds) {
    const response: AddItemToCartResponse = yield call(
      sendAddInfectiousItemToCartRequest,
      cart!.id as string,
      { infectiousAliquotId: aliquotId }
    );
    cart = response.data.data;
  }

  const infectiousItemsResponse: any = yield call(
    sendGetInfectiousOrderItemsRequest,
    cart!.id as string
  );
  cart = yield call(
    mergeInfectiousOrderItemsIntoCart,
    cart,
    infectiousItemsResponse
  );

  yield put(setUserCart({ cart }));
}

export function* handleRemoveProductFromCart(
  action: PayloadAction<CardPayload>
): Generator<any, void, any> {
  const { setUserCart } = commonSlice;
  const { toggleDialog } = dialogSagaActions;

  let cart: ICart | null = yield select(selectUserCart);

  if (!cart) {
    return;
  }

  const isInfectiousItem =
    action.payload.cartItemType === "infectious" ||
    action.payload.aliquotId !== undefined;
  let response: RemoveItemFromCartResponse;

  if (isInfectiousItem) {
    response = yield call(
      sendRemoveInfectiousItemFromCartRequest,
      cart.id as string,
      {
        infectiousAliquotId: action.payload.aliquotId ?? action.payload.sampleId,
      }
    );
  } else {
    response = yield call(
      sendRemoveItemFromCartRequest,
      cart.id as string,
      action.payload
    );
  }

  cart = response.data.data;

  if (isInfectiousItem) {
    const infectiousItemsResponse: any = yield call(
      sendGetInfectiousOrderItemsRequest,
      cart.id as string
    );
    cart = yield call(
      mergeInfectiousOrderItemsIntoCart,
      cart,
      infectiousItemsResponse
    );
  }

  yield put(setUserCart({ cart }));
  yield put(toggleDialog({ status: ToggleActionType.CLOSE }));

}

export function* handleRemoveAllProductFromCart(
) {
  const { setUserCart } = commonSlice;
  const { toggleDialog } = dialogSagaActions;
  const { updateAlert } = alertsSagaActions;
  let cart: ICart | null = yield select(selectUserCart);

  if (!cart) {
    return;
  }

  const response: RemoveItemFromCartResponse = yield call(
    sendRemoveAllItemFromCartRequest,
    cart.id as string,
  );
  cart = response.data.data;

  yield put(setUserCart({ cart }));
  yield put(toggleDialog({ status: ToggleActionType.CLOSE }));
  yield put(
    updateAlert({
      status: AlertStatusMessage.SUCCESS,
      message: `${t("cart_cleared_successfully")}`,
    })
  );
}

export function* handlePlaceOrder(
  action: PayloadAction<CardPayload>
) {
  const { setUserCart, setActiveEntity } = commonSlice;
  const { toggleDialog } = dialogSagaActions;

  let cart: ICart | null = yield select(selectUserCart);

  if (!cart) {
    return;
  }

  const { forTestingPurpose, ...basePayload } = action.payload;
  const requestPayload = getCartInfectiousOrderItems(cart).length > 0
    ? {
        ...basePayload,
        ...(typeof forTestingPurpose === "boolean" && { forTestingPurpose }),
      }
    : basePayload;

  try {
    const {data: { data }} = yield call(
      sendPlaceOrderRequest,
      cart.id as string,
      requestPayload
    );

    yield put(setActiveEntity({ activeEntity: data }));
    yield put(
      toggleDialog({
        status: ToggleActionType.OPEN,
        mode: DialogMode.CONFIRM,
      })
    );
    yield put(setUserCart({ cart: null }));
  } catch (err) {
    yield call(handleError, err);
  }
}

export function* handlePresignedImageUrlRequest(action: PayloadAction<UpdateUserAvatarPayload>) {
  const { contentType, size, originalName, blob } = action.payload;
  const { updateAlert } = alertsSagaActions;

  try {

    const response: { data: { data: string } } = yield call(sendPresignedImageUrlRequest, { contentType, size, originalName });

    yield call(fetch, response.data.data, {
      method: 'PUT',
      headers: {
        'Content-Type': contentType,
        'Content-Length': size.toString(),
        'Content-Disposition': `inline; filename="${originalName}"`,
      },
      body: blob,
    });

    yield put(
      updateAlert({
        status: AlertStatusMessage.SUCCESS,
        message: `${t("image_updated_successfully")}`,
      })
    );

    yield put(checkUserByToken());
  } catch (err) {
    yield call(handleError, err);
  }
}

function* handleError(err: any) {
  const { updateAlert } = alertsSagaActions;
  const error = err as IApiError;

  if (error?.response?.data?.error?.message?.length) {
    for (const text of error.response.data.error.message) {
      yield put(
        updateAlert({ status: AlertStatusMessage.ERROR, message: text })
      );
    }
  }
}
